// Shared onBeforeRender hook for /book, /album and /product pages: loads the
// product server-side so the page has its SEO tags (title, description,
// og:image, canonical https://aldoo.com/{route}/<pid>) and first-paint data.
//
// It also decides whether the url is the product's own. The routes map a prefix
// to a page, so /book/<pid> would render the book page whatever the product is
// — an audio CD read as a playable book. The profile's page says where a product
// belongs (contentRoutes.getContentRoute), and a url that disagrees is
// redirected here, before anything is rendered: one address per product, and
// the wrong one is never served or indexed.
import { redirect, render } from "vike/abort"
import { LoadProduct, ListAuthorProducts, PRODUCT_FIELDS } from "../api"
import { apiPid, buildContentUrl, getContentRoute, langFromPath, stripLang } from "../utils/contentRoutes"
import { generateProductPageMeta } from "../utils/pageMeta"
import { isProductOffered } from "../utils/productUtils"
import { languageUrls, productJsonLd } from "../utils/seo"

const withTimeout = (promise, ms) =>
  Promise.race([promise, new Promise((_, reject) => setTimeout(() => reject(new Error("timeout")), ms))])

export function productPidFromPath(pathname) {
  const segments = stripLang(pathname || "/").split("/").filter(Boolean)
  return { route: segments[0], pid: segments[1], sub: segments.slice(2) }
}

/**
 * Send a url to the product's own page when the prefix it was opened with is
 * not the one its profile's page calls for. The language prefix is kept, and
 * the redirect is permanent: the address does not change again. A product sent
 * without its profile is left where it was opened: nothing says where it belongs.
 */
function canonicalRoute(pathname, product, language) {
  if (!product.profile) return
  const current = stripLang(pathname).split("/").filter(Boolean)[0]
  const canonical = getContentRoute(product)
  if (!current || current === canonical) return
  const prefix = language && language !== "en" ? `/${language}` : ""
  throw redirect(`${prefix}${buildContentUrl(product)}`, 301)
}

/**
 * The author's other products (ui/MoreByAuthor), loaded with the page: they are
 * in the HTML a crawler reads, linked, and the row paints at once. Capped at a
 * second and a half; without them the row loads in the browser as before.
 */
async function authorProductsOf(product, { language, currency }) {
  const slugs = (product.author || []).map((a) => a?.pid?.replace("author.", "") || a?.slug).filter(Boolean)
  if (!slugs.length) return null
  try {
    const results = await withTimeout(
      Promise.all(slugs.map((slug) => ListAuthorProducts({ slug, page: 0, items_per_page: 10, language, currency, sortBy: "recommendationScore" }).catch(() => null))),
      1500
    )
    const all = results.flatMap((r) => r?.list || [])
    return { pid: product.pid, language, list: all.filter((p, i) => all.findIndex((q) => q.pid === p.pid) === i) }
  } catch (e) {
    return null
  }
}

export async function productOnBeforeRender(pageContext) {
  // the not-found page rendering after this hook said 404: nothing to load again
  if (pageContext.is404) return {}
  const pathname = pageContext.urlParsed?.pathname || pageContext.urlPathname || "/"
  const { pid } = productPidFromPath(pathname)
  if (!pid) return {}
  const language = langFromPath(pathname) || "en"
  // priced in the store's currency, as the first client render is
  // (pageContext.storeCurrency, set by pages/+onBeforeRender)
  const currency = pageContext.storeCurrency?.currency?.toLowerCase()
  let product
  try {
    product = await withTimeout(LoadProduct({ pid: apiPid(pid), language, currency, fields: PRODUCT_FIELDS }), 4000)
    // the API says there is no such product: a 404, not an empty page served as a 200
    if (product?.error) throw render(404)
    if (!product?.pid) return {}
    // a product the store no longer offers renders the unavailable notice: it
    // is handed to the page so the notice paints at once, but it gets none of
    // the product's SEO tags — the page is not that product any more
    canonicalRoute(pathname, product, language)

    if (!isProductOffered(product)) return { storefrontProduct: product }
    const meta = generateProductPageMeta(product)
    const authorProducts = await authorProductsOf(product, { language, currency })
    // structured data: the product and its offer, and the trail through its first shelf
    const { canonicalUrl } = languageUrls(pathname, pageContext.supportedLanguages)
    const categories = pageContext.storefrontCatalog?.categories || []
    const shelf = (product.category || []).map((slug) => categories.find((c) => c.categoryId === slug)).find(Boolean)
    const jsonLd = productJsonLd(product, { language, url: canonicalUrl, categoryName: shelf?.name, categoryPath: shelf ? `/category/${shelf.categoryId}` : undefined })
    return { ...meta, metadata: { ...meta.metadata, jsonLd }, storefrontProduct: product, storefrontAuthorProducts: authorProducts }
  } catch (error) {
    // `redirect()` is thrown, not returned: it has to reach vike rather than
    // the log below. Vike stamps the object it throws with `_isAbortError`.
    if (error && typeof error === "object" && "_isAbortError" in error) throw error
    console.error("Storefront product onBeforeRender:", error?.message || error)
    return {}
  }
}
